import { FilterRadioGroup } from "./FilterRadioGroup.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Cover, Flex, Heading1, Stack, TextButton } from "@myphx-engineering/phx-design-system";
import { jsx, jsxs } from "react/jsx-runtime";

//#region src/client/components/consent-filter/ConsentFilterCover.tsx
const ConsentFilterCover = ({ isOpen, onClose, consents, currentFilters, onFiltersApplied }) => {
	const { t } = useTranslation("cons");
	const statusOptions = useMemo(() => [
		{
			value: "ALL",
			label: t("consentStatus.all")
		},
		{
			value: "ACTIVE",
			label: t("consentStatus.accepted")
		},
		{
			value: "REVOKED",
			label: t("consentStatus.revoked")
		},
		{
			value: "DECLINED",
			label: t("consentStatus.declined")
		}
	], [t]);
	const typeOptions = [
		{
			value: "ALL",
			label: "All"
		},
		{
			value: "REQUIRED",
			label: t("consents.required")
		},
		{
			value: "OPTIONAL",
			label: t("consents.optional")
		}
	];
	const [tempFilters, setTempFilters] = useState(currentFilters);
	useEffect(() => {
		if (isOpen) setTempFilters(currentFilters);
	}, [isOpen, currentFilters]);
	const handleReset = useCallback(() => {
		setTempFilters({
			status: "ACTIVE",
			type: "ALL"
		});
	}, []);
	const filteredConsents = useMemo(() => {
		let filtered = [...consents];
		if (tempFilters.status !== "ALL") filtered = filtered.filter((consent) => consent.status === tempFilters.status);
		if (tempFilters.type !== "ALL") {
			if (tempFilters.type === "REQUIRED") filtered = filtered.filter((consent) => consent.document.required);
			else if (tempFilters.type === "OPTIONAL") filtered = filtered.filter((consent) => !consent.document.required);
		}
		return filtered;
	}, [consents, tempFilters]);
	const activeFilterCount = useMemo(() => {
		let count = 0;
		if (tempFilters.status !== "ACTIVE") count++;
		if (tempFilters.type !== "ALL") count++;
		return count;
	}, [tempFilters]);
	const handleSubmit = useCallback(() => {
		onFiltersApplied(filteredConsents, tempFilters, statusOptions.find((opt) => opt.value === tempFilters.status)?.label || t("consentStatus.all"), activeFilterCount);
		onClose();
	}, [
		activeFilterCount,
		filteredConsents,
		onClose,
		onFiltersApplied,
		statusOptions,
		t,
		tempFilters
	]);
	return /* @__PURE__ */ jsx(Cover, {
		visible: isOpen,
		allowScroll: true,
		children: /* @__PURE__ */ jsxs(Stack, {
			flex: "1",
			justify: "space-between",
			h: "100%",
			children: [/* @__PURE__ */ jsxs(Stack, {
				gap: "lg",
				children: [/* @__PURE__ */ jsxs(Flex, {
					justify: "space-between",
					align: "center",
					children: [/* @__PURE__ */ jsx(Heading1, { children: t("consents.filter.heading") }), /* @__PURE__ */ jsx(TextButton, {
						disabled: activeFilterCount === 0,
						onClick: handleReset,
						children: t("consents.filter.reset.button")
					})]
				}), /* @__PURE__ */ jsxs(Stack, {
					gap: "xxl",
					children: [/* @__PURE__ */ jsx(FilterRadioGroup, {
						title: t("consents.filter.status.title"),
						options: statusOptions,
						selection: tempFilters.status,
						onChange: (value) => setTempFilters((prev) => ({
							...prev,
							status: value
						}))
					}), /* @__PURE__ */ jsx(FilterRadioGroup, {
						title: t("consents.filter.type.title"),
						options: typeOptions,
						selection: tempFilters.type,
						onChange: (value) => setTempFilters((prev) => ({
							...prev,
							type: value
						}))
					})]
				})]
			}), /* @__PURE__ */ jsx(Button, {
				onClick: handleSubmit,
				children: filteredConsents.length === 0 ? t("consents.filter.noConsentsFound") : t("consents.filter.showMultipleConsents", { count: filteredConsents.length })
			})]
		})
	});
};

//#endregion
export { ConsentFilterCover };